import { Position } from '@/shared/ui/data/EditableGrid/types/core.type';
import { CellValue } from '@/shared/ui/data/EditableGrid/types/cell.types';
import {
  BulkEditCommand,
  canRedo,
  canUndo,
  CellEditCommand,
  createHistoryStack,
  HistoryStack,
  pushCommand,
  redoCommand,
  undoCommand,
} from '@/shared/ui/data/EditableGrid/lib/history';
import React, { useCallback, useRef, useState } from 'react';

interface IOptions {
  onCellValueChange: (position: Position, newValue: CellValue) => void;
  onBulkValueChange: (changes: ReadonlyArray<{ position: Position; value: CellValue }>) => void;
  maxHistorySize: number;
}

export function useHistory(options: IOptions) {
  const { onCellValueChange, onBulkValueChange, maxHistorySize } = options;

  const [history, setHistory] = useState<HistoryStack>(() => createHistoryStack(maxHistorySize));

  const executingCommandId = useRef<string | null>(null);

  const recordCellEdit = useCallback(
    (position: Position, oldValue: CellValue, newValue: CellValue) => {
      if (oldValue === newValue) return;

      const commandId = `cell-edit-${Date.now()}-${Math.random()}`;

      const command: CellEditCommand = {
        id: commandId,
        timestamp: Date.now(),
        type: 'editCell',
        position,
        oldValue,
        newValue,
        execute: () => {
          if (executingCommandId.current === commandId) return;
          executingCommandId.current = commandId;
          onCellValueChange(position, newValue);
          executingCommandId.current = null;
        },
        undo: () => {
          if (executingCommandId.current === commandId) return;
          executingCommandId.current = commandId;
          onCellValueChange(position, oldValue);
          executingCommandId.current = null;
        },
      };

      setHistory((prev) => pushCommand(prev, command));
    },
    [onCellValueChange]
  );

  const recordBulkEdit = useCallback(
    (
      changes: ReadonlyArray<{
        position: Position;
        oldValue: CellValue;
        newValue: CellValue;
      }>
    ) => {
      if (changes.length === 0) return;

      const commandId = `bulk-edit-${Date.now()}-${Math.random()}`;

      const command: BulkEditCommand = {
        id: commandId,
        timestamp: Date.now(),
        type: 'bulkEdit',
        changes,
        execute: () => {
          if (executingCommandId.current === commandId) return;
          executingCommandId.current = commandId;
          onBulkValueChange(changes.map((c) => ({ position: c.position, value: c.newValue })));
          executingCommandId.current = null;
        },
        undo: () => {
          if (executingCommandId.current === commandId) return;
          executingCommandId.current = commandId;
          onBulkValueChange(changes.map((c) => ({ position: c.position, value: c.oldValue })));
          executingCommandId.current = null;
        },
      };

      setHistory((prev) => pushCommand(prev, command));
    },
    [onBulkValueChange]
  );

  const undo = useCallback(() => {
    if (!canUndo(history)) return;

    const { history: newHistory, command } = undoCommand(history);
    if (command) {
      command.undo();
      setHistory(newHistory);
    }
  }, [history]);

  const redo = useCallback(() => {
    if (!canRedo(history)) return;

    const { history: newHistory, command } = redoCommand(history);
    if (command) {
      command.execute();
      setHistory(newHistory);
    }
  }, [history]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const isMac = navigator.userAgent.includes('Mac');
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      const isUndoKey = modifier && e.key.toLowerCase() === 'z' && !e.shiftKey;

      if (isUndoKey) {
        if (canUndo(history)) {
          e.preventDefault();
          undo();
        }
        return;
      }

      const isModifierShiftZ = modifier && e.key.toLowerCase() === 'z' && e.shiftKey;
      const isCtrlY = !isMac && e.ctrlKey && e.key.toLowerCase() === 'y';
      const isRedoKey = isModifierShiftZ || isCtrlY;

      if (isRedoKey) {
        if (canRedo(history)) {
          e.preventDefault();
          redo();
        }
        return;
      }
    },
    [history, undo, redo]
  );

  return {
    canUndo: () => canUndo(history),
    canRedo: () => canRedo(history),
    undo,
    redo,
    recordCellEdit,
    recordBulkEdit,
    handleKeyDown,
    undoStackSize: history.undoStack.length,
    redoStackSize: history.redoStack.length,
  };
}
